import React, {useEffect, useMemo, useRef, useState} from 'react';
import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {useChatContext} from "../context/ChatContext";
import type {ChatSession} from "../types/chatTypes";
import moment from "moment";
import LightEditSquare from '../../../components/icons/svg/LightEditSquare.svg?react';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import {generatePath, useLocation, useNavigate} from "react-router";
import {routes} from "../../../routing/routes";
import {useStores} from "../../../stores";
import {ActionsMenu} from './ActionsMenu/ActionsMenu';
import {useRenameSessionMutation} from '../hooks/useRenameSessionMutation';

type SessionGroup = {
    label: string;
    sessions: ChatSession[];
};

const groupSessionsByDate = (sessions: ChatSession[]): SessionGroup[] => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const groups: Map<string, ChatSession[]> = new Map();

    // Sort sessions by updated_at descending (most recent first)
    const sortedSessions = [...sessions].sort((a, b) => b.updated_at.getTime() - a.updated_at.getTime());
    for (const session of sortedSessions) {
        const sessionDate = new Date(session.updated_at.getFullYear(), session.updated_at.getMonth(), session.updated_at.getDate());

        let groupLabel: string;
        if (sessionDate.getTime() === today.getTime()) {
            groupLabel = 'Today';
        } else if (sessionDate.getTime() === yesterday.getTime()) {
            groupLabel = 'Yesterday';
        } else {
            // Format as "Monday, 23 Jan 2023" (in appropriate locale)
            const m = moment(session.updated_at);
            groupLabel = `${m.format('dddd')}, ${m.format('ll')}`
        }
        if (!groups.has(groupLabel)) {
            groups.set(groupLabel, []);
        }
        groups.get(groupLabel)!.push(session);
    }

    // Convert map to array while preserving order
    return Array.from(groups.entries()).map(([label, sessions]) => ({
        label,
        sessions,
    }));
};

export const Sidebar: React.FC = () => {
    const {
        sessions,
        currentSession,
        selectSession,
        startNewChat,
        updateSessionName,
    } = useChatContext();
    const groupedSessions = useMemo(() => groupSessionsByDate(sessions), [sessions]);
    const {bagStore} = useStores();
    const navigate = useNavigate();
    const location = useLocation();
    const onDashboardsPage = /\/chat\/dashboards\/?$/.test(location.pathname);
    const [renameSessionName, setRenameSessionName] = useState('');
    const [renameSessionId, setRenameSessionId] = useState<string | null>('');
    const {mutate: renameSession} = useRenameSessionMutation();

    function handleSessionIdSelection(sessionId: string, sessionName: string) {
        setRenameSessionName(sessionName);
        setRenameSessionId(sessionId);
    }

    const renameSessionNameRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (!renameSessionId) return;
        const timeout = setTimeout(() => {
            renameSessionNameRef.current?.focus();
            renameSessionNameRef.current?.select();
        }, 100);
        return () => clearTimeout(timeout);
    }, [renameSessionId]);

    function handleRenameSessionSubmit(appName: string) {
        if (!renameSessionId) return;
        if (!renameSessionName) return;
        renameSession(
            {sessionId: renameSessionId, displayName: renameSessionName, appName},
            {
                onSuccess: () => {
                    updateSessionName(renameSessionId, renameSessionName);
                    setRenameSessionId(null);
                    setRenameSessionName('');
                },
            },
        );
    }

    function handleRenameSessionCancel() {
        setRenameSessionId(null);
        setRenameSessionName('');
    }

    return <div className="sidebar">
        {/* Header */}
        <List className="menuList sidebar-actions" disablePadding>
            <ListItemButton onClick={() => {
                // From the dashboards page, first return to the chat route (startNewChat
                // only clears state; its URL sync is a no-op outside /chat).
                if (onDashboardsPage && bagStore.bagId) {
                    navigate(generatePath(routes.chat, {id: String(bagStore.bagId)}));
                }
                startNewChat();
            }}>
                <ListItemIcon><LightEditSquare/></ListItemIcon>
                <ListItemText primary="New chat"/>
            </ListItemButton>
            <ListItemButton
                selected={onDashboardsPage}
                onClick={() => {
                    if (bagStore.bagId) {
                        navigate(generatePath(routes.chat_dashboards, {id: String(bagStore.bagId)}));
                    }
                }}
            >
                <ListItemIcon><SpaceDashboardOutlinedIcon fontSize="small"/></ListItemIcon>
                <ListItemText primary="Dashboards"/>
            </ListItemButton>
        </List>

        {/* Session List */}
        <List className="menuList sidebar-items" sx={{pr: 0.5}}>
            {groupedSessions.map((group, i) => <React.Fragment key={group.label}>
                <ListSubheader className={i === 0 ? 'first' : ''}>{group.label}</ListSubheader>
                {group.sessions.map((session) => (

                    <ListItem sx={{
                        justifyContent: 'space-between',
                        pl: 1.5,
                        borderRadius: '8px',
                        bgcolor: currentSession?.id === session.id ? 'action.selected' : 'transparent',
                        '&:hover': {bgcolor: 'action.hover'}
                    }}
                              key={session.id} disablePadding
                    >

                        {session.id === renameSessionId ?
                            <TextField inputRef={renameSessionNameRef} size="small" fullWidth
                                       value={renameSessionName}
                                       onChange={(e) => setRenameSessionName(e.target.value)}
                                       onKeyDown={(e) => {
                                           if (e.key === 'Enter') handleRenameSessionSubmit(session.appName);
                                           if (e.key === 'Escape') handleRenameSessionCancel();
                                       }}
                                       onBlur={() => {
                                           handleRenameSessionSubmit(session.appName);
                                       }}
                                       sx={{
                                           width: '100%',
                                           flex: 1,
                                           fontSize: 14,
                                           '& .MuiInputBase-input.MuiOutlinedInput-input': {
                                               paddingLeft: 0,
                                               fontSize: 14
                                           },
                                           '& .MuiOutlinedInput-notchedOutline': {border: 0, paddingLeft: 0},
                                           '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                               border: 0,
                                               paddingLeft: 0
                                           },
                                           '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                               border: 0,
                                               paddingLeft: 0
                                           },
                                       }}
                            /> : <Tooltip
                                title={moment(session.updated_at).format('LLLL')}
                                placement="right"
                                arrow><Typography onClick={() => selectSession(session)} sx={{
                                    flex: 1,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '100%',
                                    cursor: 'pointer',
                                }} variant="body2">{session.name}</Typography></Tooltip>}

                        <ActionsMenu
                            sessionId={session.id}
                            sessionName={session.name}
                            appName={session.appName}
                            onRenameSession={() => handleSessionIdSelection(session.id, session.name)}
                        />
                    </ListItem>

                ))}
            </React.Fragment>)}
        </List>
    </div>
};
